import React from 'react';
import {
  Plane, Ship, Barcode, FileText, Calendar, User, Building2, Boxes,
  IndianRupee, DollarSign, Scale, Container, Hash, CalendarClock,
  Download, CreditCard, CheckCircle2, Radar
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import './ShipmentCard.css';

const FIELD_ICON = {
  'AWB No.': Barcode, 'B/L Number': Barcode,
  'S/B No.': FileText, 'S/B Date': Calendar,
  Consignee: User, 'Buyer Name': Building2, Buyer: Building2,
  'Total Pkt': Boxes, 'FOB Value': IndianRupee, 'FC Value': DollarSign,
  'Gr. Weight': Scale, 'Ch. Weight': Container,
  'Flight No.': Plane, 'Flight Date': CalendarClock,
  'Container No': Container, 'Container Qty': Boxes,
};

const Field = ({ label, value }) => {
  const Icon = FIELD_ICON[label] || Hash;
  return (
    <div className="flex items-start gap-2.5 min-w-0">
      <span className="ql-card__fieldicon"><Icon size={14} /></span>
      <div className="min-w-0">
        <p className="text-[11px] leading-4 text-slate-400 dark:text-slate-500 font-medium">{label}</p>
        <p className="text-[13px] leading-5 font-bold text-slate-800 dark:text-slate-100 break-words" title={String(value ?? '-')}>
          {value === '' || value === null || value === undefined ? '-' : value}
        </p>
      </div>
    </div>
  );
};

const Action = ({ tone, icon: Icon, children, ...rest }) => (
  <button type="button" className={`ql-card__action ql-card__action--${tone}`} {...rest}>
    <Icon size={14} />
    <span>{children}</span>
  </button>
);

/**
 * One shipment, as the user dashboard lists them.
 * `fields` is the label/value pairs to print, so air and sea can differ.
 */
const ShipmentCard = ({
  shipment,
  fields = [],
  isSea = false,
  selectable = false,
  selected = false,
  onSelect,
  onTrack,
  documents = [],          // [{ label, tone, onClick }]
  payment = null,          // { paid: bool, onPay } | null
  flag,                    // (countryName) => emoji
  highlighted = false,
}) => {
  const { isDarkMode } = useTheme();
  const Mode = isSea ? Ship : Plane;
  const hero = isDarkMode
    ? '/images/theme-assets/shipment-card-hd-dark.png'
    : '/images/theme-assets/shipment-card-hd-light.png';
  const carrier = isSea ? (shipment.vesselName || shipment.containerNo) : shipment.flightNo;

  return (
    <article className={`ql-card${highlighted ? ' is-highlighted' : ''}${selected ? ' is-selected' : ''}`}>
      {/* The route strip spans only the detail column; the action rail runs the
          full height of the card beside it. */}
      <div className="ql-card__main">
      <header className="ql-card__route">
        <div className="ql-card__routebg" style={{ backgroundImage: `url(${hero})` }} aria-hidden="true" />

        <div className="relative flex items-center gap-4 sm:gap-5 px-4 sm:px-5 py-4">
          {/* The slot is always there, with or without the box in it — a card
              that cannot be selected would otherwise sit 12px off the ones
              that can, and the pills would not line up between them. */}
          <span className="ql-card__checkslot">
            {selectable && (
              <input
                type="checkbox"
                checked={selected}
                onChange={(e) => onSelect?.(e.target.checked)}
                className="ql-card__check"
                aria-label="Select shipment"
              />
            )}
          </span>

          <div className="flex-1 min-w-0 flex items-center gap-3">
            <div className="ql-card__end">
              <p className="ql-card__port">
                {shipment.origin || shipment.portCode || '—'}
                <span className="ql-card__flag">{flag?.(shipment.originCountry || 'India')}</span>
              </p>
              <p className="ql-card__city" title={`${shipment.originCity || 'Delhi'}, ${shipment.originCountry || 'India'}`}>
                {shipment.originCity || 'Delhi'}, {shipment.originCountry || 'India'}
              </p>
            </div>

            <div className="ql-card__leg">
              <span className="ql-card__dash" />
              {carrier && carrier !== '-' && (
                <span className="ql-card__carrier"><Mode size={12} />{carrier}</span>
              )}
              <span className="ql-card__dash" />
            </div>

            <div className="ql-card__end text-right">
              <p className="ql-card__port ql-card__port--right">
                <span className="ql-card__flag">{flag?.(shipment.destCountry)}</span>
                {shipment.dest || '—'}
              </p>
              <p className="ql-card__city" title={`${shipment.destCity || ''}${shipment.destCountry ? `, ${shipment.destCountry}` : ''}`}>
                {shipment.destCity}{shipment.destCountry ? `, ${shipment.destCountry}` : ''}
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-4 px-4 sm:px-5 py-4">
          {fields.map((f) => <Field key={f.label} label={f.label} value={f.value} />)}
        </div>
      </div>

      <div className="ql-card__rail">
          {onTrack && <Action tone="blue" icon={Radar} onClick={onTrack}>Track Shipment</Action>}

          {documents.map((d) => (
            <Action key={d.label} tone={d.tone} icon={Download} onClick={d.onClick}>{d.label}</Action>
          ))}

          {payment && (payment.paid ? (
            <span className="ql-card__paid"><CheckCircle2 size={14} /> Paid</span>
          ) : (
            <Action tone="pay" icon={CreditCard} onClick={payment.onPay}>Pay Now</Action>
          ))}
      </div>
    </article>
  );
};

export default ShipmentCard;
